import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Heading,
  SelectField,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import { Link } from 'react-router-dom';
import Loadingmini from '../components/loadingmini';
import GigTenantSummary from '../components/GigTenantSummary';
import { api3Get } from '../api/api3Client';

const RECORD_FILTERS = [
  { value: 'not_removed', label: 'On record' },
  { value: 'removed', label: 'Removed (retained)' },
  { value: 'all', label: 'Everything' },
];

const VERDICT_FILTERS = [
  { value: 'all', label: 'Any verdict' },
  { value: 'keep', label: 'Would keep' },
  { value: 'would_disconnect', label: 'Would disconnect' },
];

const badge = (background, color) => ({
  background,
  color,
  padding: '2px 6px',
  borderRadius: '4px',
  whiteSpace: 'nowrap',
});

function voteStyle(vote) {
  if (vote === 'cull') return badge('#fde8e8', '#9b1c1c');
  if (vote === 'keep') return badge('#e6f4ea', '#1e4620');
  return badge('#eee', '#444');
}

function verdictStyle(verdict) {
  if (verdict === 'would_disconnect') return badge('#fde8e8', '#9b1c1c');
  if (verdict === 'keep') return badge('#e6f4ea', '#1e4620');
  return badge('#eee', '#444');
}

function recordStyle(recordStatus) {
  if (recordStatus === 'removed') return badge('#eee', '#444');
  return badge('#e8f0fe', '#1a4480');
}

function formatTimestamp(ts) {
  if (ts === undefined || ts === null || ts === '') return '—';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return String(ts);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function IdCell({ value }) {
  if (!value) return '—';
  return (
    <Text
      fontSize="0.75rem"
      style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
      title={value}
    >
      {value}
    </Text>
  );
}

function RecordStatusCell({ row }) {
  const recordStatus = row.recordStatus || 'not_removed';
  const providerStatus = row.providerConnectionStatus;

  return (
    <View>
      <span style={recordStyle(recordStatus)}>
        {recordStatus === 'removed' ? 'removed' : 'on record'}
      </span>
      {recordStatus === 'removed' && row.providerAcctRemovedAt ? (
        <Text fontSize="0.8rem" display="block">
          removed {formatTimestamp(row.providerAcctRemovedAt)}
          {row.providerAcctRemovalApprovedBy
            ? ` · ${row.providerAcctRemovalApprovedBy}`
            : ''}
        </Text>
      ) : null}
      {recordStatus === 'removed' && row.providerAcctRemovalApprovedAt ? (
        <Text fontSize="0.8rem" display="block">
          approved {formatTimestamp(row.providerAcctRemovalApprovedAt)}
        </Text>
      ) : null}
      {providerStatus ? (
        <Text fontSize="0.8rem" display="block">
          Provider link: {providerStatus}
        </Text>
      ) : recordStatus === 'not_removed' ? (
        <Text fontSize="0.8rem" display="block" color="#666">
          Provider link: unknown
        </Text>
      ) : null}
    </View>
  );
}

function FleetRules({ rules }) {
  const [open, setOpen] = useState(false);
  if (!rules?.length) return null;
  return (
    <View marginTop="1rem">
      <Button size="small" onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'Show'} the cull rule in effect for each fleet
      </Button>
      {open ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
          <thead>
            <tr>
              <th align="left">Fleet</th>
              <th align="left">Culling</th>
              <th align="left">Window</th>
              <th align="left">Rule</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.fleetId}>
                <td>
                  {r.fleetCode} ({r.name})
                </td>
                <td>
                  <span style={r.cullEnabled ? voteStyle('cull') : voteStyle('keep')}>
                    {r.cullEnabled ? 'enabled' : 'disabled'}
                  </span>
                </td>
                <td>
                  {r.windowDays} days
                  {r.flooredForSafety ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      (raised from {r.configuredStaleDays} by the 30-day floor)
                    </Text>
                  ) : null}
                </td>
                <td>{r.ruleText}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </View>
  );
}

function AccountDetail({ row }) {
  return (
    <View padding="0.5rem 0.5rem 0.75rem 2rem" backgroundColor="#fafafa">
      <Text fontSize="0.9rem">
        Gig provider user {row.jobUserId} · gig account {row.jobAccountId} · gig tenant{' '}
        {row.jobApiInfoId} ({row.jobApi})
      </Text>
      <Text fontSize="0.9rem">
        Last list sync: {formatTimestamp(row.lastListSyncAt)} · last full verify:{' '}
        {formatTimestamp(row.lastFullVerifyAt)} · row updated: {formatTimestamp(row.updatedAt)}
      </Text>
      {row.lastErrorCode ? (
        <Text fontSize="0.9rem">Last error code: {row.lastErrorCode}</Text>
      ) : null}

      <Heading level={6} marginTop="0.5rem">
        Active memberships
      </Heading>
      {row.activeMemberships?.length ? (
        <Text fontSize="0.9rem">
          {row.activeMemberships
            .map((m) => `${m.fleetCode || m.fleetId} (user ${m.userId})`)
            .join(', ')}
        </Text>
      ) : (
        <Text fontSize="0.9rem">None — reconciliation path.</Text>
      )}

      <Heading level={6} marginTop="0.5rem">
        Per-fleet votes (what the next perioddata run would cast)
      </Heading>
      {row.votes?.length ? (
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {row.votes.map((v) => (
              <tr key={v.fleetId}>
                <td style={{ paddingRight: '0.75rem' }}>{v.fleetCode}</td>
                <td style={{ paddingRight: '0.75rem' }}>
                  <span style={voteStyle(v.vote)}>{v.vote}</span>
                </td>
                <td style={{ paddingRight: '0.75rem' }}>
                  {v.lastDrove
                    ? `last drove ${formatTimestamp(v.lastDrove)}`
                    : 'no reservation on record'}
                </td>
                <td>{v.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Text fontSize="0.9rem">No votes — no active memberships.</Text>
      )}

      <Text marginTop="0.5rem" fontSize="0.9rem">
        Overall: <span style={verdictStyle(row.verdict)}>{row.verdict}</span> ({row.verdictPass}) —{' '}
        {row.verdictReason}
      </Text>

      {row.openProposal ? (
        <Alert variation="info" marginTop="0.5rem">
          Open proposal #{row.openProposal.id} ({row.openProposal.state} / {row.openProposal.action}
          ): {row.openProposal.reason}. Review in the{' '}
          <Link to="/admin/gig-disconnect">disconnect queue</Link>.
        </Alert>
      ) : null}
    </View>
  );
}

export default function AdminGigAccounts() {
  const [recordStatus, setRecordStatus] = useState('not_removed');
  const [verdictFilter, setVerdictFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [note, setNote] = useState('');
  const [expanded, setExpanded] = useState(null);
  const limit = 100;

  async function loadList(nextOffset) {
    const off = typeof nextOffset === 'number' ? nextOffset : offset;
    setLoading(true);
    setError('');
    try {
      const res = await api3Get('/admin/portal/gig-accounts', {
        recordStatus,
        verdictFilter,
        q: query,
        limit,
        offset: off,
      });
      setRows(res.accounts || []);
      setRules(res.fleetRules || []);
      setTenants(res.tenantSummaries || []);
      setTotal(res.total || 0);
      setNote(res.note || '');
      setOffset(off);
      setExpanded(null);
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadList(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordStatus, verdictFilter]);

  return (
    <View padding="1rem">
      <Heading level={3}>Admin: Gig Account Status</Heading>
      <Text>
        One row per <strong>gig account</strong> (a single Uber/Lyft/… link). A{' '}
        <strong>gig provider user</strong> (<code>job_user_id</code>) may hold several gig
        accounts. &ldquo;On record&rdquo; means <code>provider_acct_removed_at</code> is null —
        not that the link is live at the provider. This page does not disconnect anything.
        Approvals live on the <Link to="/admin/gig-disconnect">disconnect queue</Link>.
      </Text>
      <Text fontSize="0.9rem" marginTop="0.35rem" color="#444">
        <strong>Last reservation</strong> is the latest{' '}
        <code>insurance_schedule_table.end</code> for that person across all fleets (real
        booked coverage), not <code>res_ledger</code>. Blank means no schedule rows for that
        person.
      </Text>

      <FleetRules rules={rules} />
      <GigTenantSummary tenants={tenants} />

      <Flex direction="row" gap="0.5rem" alignItems="flex-end" wrap="wrap" marginTop="1rem">
        <SelectField
          label="Record status"
          value={recordStatus}
          onChange={(e) => setRecordStatus(e.target.value)}
        >
          {RECORD_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Verdict (this page)"
          value={verdictFilter}
          onChange={(e) => setVerdictFilter(e.target.value)}
        >
          {VERDICT_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </SelectField>
        <TextField
          label="Search"
          placeholder="Ago user id, gig provider user id, gig account id, gig source"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button onClick={() => loadList(0)} isLoading={loading}>
          Search / Refresh
        </Button>
      </Flex>

      <Text marginTop="0.75rem">
        Showing {rows.length} of {total} matching row
        {total === 1 ? '' : 's'} (offset {offset}).
      </Text>
      {note ? (
        <Text fontSize="0.85rem" marginTop="0.25rem">
          {note}
        </Text>
      ) : null}

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {loading ? <Loadingmini /> : null}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
        <thead>
          <tr>
            <th align="left">Driver</th>
            <th align="left">Gig source</th>
            <th align="left">Gig provider user</th>
            <th align="left">Gig account</th>
            <th align="left">Record</th>
            <th align="left">Last reservation</th>
            <th align="left">Memberships</th>
            <th align="left">Verdict</th>
            <th align="left">Proposal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr
                style={{
                  cursor: 'pointer',
                  background: expanded === row.id ? '#eef6ff' : undefined,
                }}
                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
              >
                <td>
                  {row.driverName || '—'}
                  {row.userId ? ` (${row.userId})` : ''}
                  {row.personId && row.personId !== row.userId ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      person {row.personId}
                    </Text>
                  ) : null}
                </td>
                <td>{row.platform || '—'}</td>
                <td>
                  <IdCell value={row.gigProviderUserId || row.jobUserId} />
                </td>
                <td>
                  <IdCell value={row.gigAccountId || row.jobAccountId} />
                </td>
                <td>
                  <RecordStatusCell row={row} />
                </td>
                <td>
                  {formatTimestamp(row.lastReservationAt)}
                  {row.hasCurrentReservation ? (
                    <Text as="span" fontSize="0.8rem">
                      {' '}
                      (current)
                    </Text>
                  ) : null}
                </td>
                <td>
                  {(row.activeMemberships || [])
                    .map((m) => m.fleetCode || m.fleetId)
                    .join(', ') || '—'}
                </td>
                <td>
                  <span style={verdictStyle(row.verdict)}>{row.verdict}</span>
                </td>
                <td>
                  {row.openProposal ? `#${row.openProposal.id} (${row.openProposal.state})` : '—'}
                </td>
              </tr>
              {expanded === row.id ? (
                <tr>
                  <td colSpan={9}>
                    <AccountDetail row={row} />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
          {!loading && !rows.length ? (
            <tr>
              <td colSpan={9}>
                <Text padding="1rem">Nothing in this view.</Text>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Flex direction="row" gap="0.5rem" marginTop="1rem">
        <Button
          isDisabled={loading || offset <= 0}
          onClick={() => loadList(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          isDisabled={loading || offset + limit >= total}
          onClick={() => loadList(offset + limit)}
        >
          Next
        </Button>
      </Flex>
    </View>
  );
}
